import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';
import { isImageKey } from '../../media-meta';
import '../../components/ui/album-view';
import type { CadenceAlbumView } from '../../components/ui/album-view';

interface Tag { id: string; name: string; color: string; }
interface Album {
  id: string; name: string; description: string | null; item_count?: number; pending_count?: number;
  cover_url?: string | null; cover_type?: 'photo' | 'video' | null;
}

const mediaUrl = (key: string | null | undefined) => (key ? `/api/v1/media/${key}` : '');

/**
 * <cadence-event-gallery> — the Gallery tab on an event. Lists the event's albums (ordinary
 * media albums flagged with this event), opens one in the shared <cadence-album-view>. Reuses
 * the whole media-gallery stack via the /events/:id/gallery API; gated by events.edit.
 */
@customElement('cadence-event-gallery')
export class CadenceEventGallery extends LitElement {
  @property() eventId = '';
  @property({ type: Boolean }) canEdit = false;

  @state() private loading = true;
  @state() private albums: Album[] = [];
  @state() private allTags: Tag[] = [];
  @state() private openAlbum: Album | null = null;

  @state() private modalOpen = false;
  @state() private editId: string | null = null;
  @state() private form = { name: '', description: '' };
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .bar { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-bottom: var(--space-4); }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
    .album-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; background: var(--color-bg-primary); transition: border-color 0.15s; position: relative; }
    .album-card:hover { border-color: var(--color-primary); }
    .album-cover { aspect-ratio: 4/3; background: var(--color-bg-secondary); display: grid; place-items: center; color: var(--color-text-secondary); overflow: hidden; position: relative; }
    .album-cover img, .album-cover video { width: 100%; height: 100%; object-fit: cover; }
    .album-cover svg { width: 38px; height: 38px; opacity: 0.5; }
    .album-cover .cover-play { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .album-cover .cover-play span { background: rgba(0,0,0,0.5); color: #fff; border-radius: 999px; width: 34px; height: 34px; display: grid; place-items: center; }
    .album-cover .cover-play svg { width: 18px; height: 18px; opacity: 1; }
    .album-meta { padding: 10px 12px; }
    .album-meta .name { font-weight: 600; word-break: break-word; }
    .album-meta .sub { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .pending-pill { position: absolute; top: 8px; right: 8px; background: var(--color-danger); color: #fff; border-radius: 999px; font-size: 0.7rem; padding: 2px 8px; font-weight: 700; }
    .efield { margin-bottom: var(--space-3); }
    .efield label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); resize: vertical; min-height: 70px; }
  `;

  private get base() { return `/events/${this.eventId}/gallery`; }
  private loaded = false;

  updated() { if (this.eventId && !this.loaded) { this.loaded = true; this.load(); } }

  private async load() {
    this.loading = true;
    try {
      const [albums, tags] = await Promise.all([
        api.get<Album[]>(`${this.base}/albums`),
        api.get<Tag[]>(`${this.base}/tags`).catch(() => []),
      ]);
      this.albums = albums; this.allTags = tags;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private openAlbumView(a: Album) { this.openAlbum = a; }
  private closeAlbumView() { this.openAlbum = null; this.load(); }
  private mergeTag(tag: Tag) {
    if (!this.allTags.some((tg) => tg.id === tag.id)) {
      this.allTags = [...this.allTags, tag].sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  private openNew() { this.editId = null; this.form = { name: '', description: '' }; this.modalOpen = true; }
  private openEdit(a: Album) { this.editId = a.id; this.form = { name: a.name, description: a.description ?? '' }; this.modalOpen = true; }
  private async save() {
    if (!this.form.name.trim()) { showToast(t('events.toast.albumNameRequired'), 'error'); return; }
    this.saving = true;
    const body = { name: this.form.name.trim(), description: this.form.description.trim() || null };
    try {
      if (this.editId) {
        await api.put(`${this.base}/albums/${this.editId}`, body);
        if (this.openAlbum?.id === this.editId) {
          this.openAlbum = { ...this.openAlbum, ...body };
          (this.renderRoot.querySelector('cadence-album-view') as CadenceAlbumView | null)?.reload();
        }
      } else {
        await api.post(`${this.base}/albums`, body);
      }
      this.modalOpen = false;
      await this.load();
      showToast(t('events.toast.albumSaved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  private renderCover(a: Album) {
    if (!a.cover_url) return icon('image', 38);
    const inner = isImageKey(a.cover_url)
      ? html`<img src=${mediaUrl(a.cover_url)} alt=${a.name} loading="lazy" />`
      : html`<video src=${mediaUrl(a.cover_url)} preload="metadata" muted></video>`;
    return html`${inner}${a.cover_type === 'video' ? html`<div class="cover-play"><span>${icon('play', 18)}</span></div>` : ''}`;
  }

  render() {
    if (this.loading) return html`<div class="panel"><cadence-loading></cadence-loading></div>`;
    if (this.openAlbum) return this.renderAlbumView();
    return html`
      <div class="panel">
        ${this.canEdit ? html`<div class="bar"><cadence-button size="sm" @click=${() => this.openNew()}>${t('events.gallery.newAlbum')}</cadence-button></div>` : ''}
        ${this.albums.length ? html`
          <div class="grid">
            ${this.albums.map((a) => html`
              <div class="album-card" @click=${() => this.openAlbumView(a)}>
                ${this.canEdit && (a.pending_count ?? 0) > 0 ? html`<span class="pending-pill">${t('events.gallery.pending', { count: a.pending_count ?? 0 })}</span>` : ''}
                <div class="album-cover">${this.renderCover(a)}</div>
                <div class="album-meta">
                  <div class="name">${a.name}</div>
                  <div class="sub">${(a.item_count ?? 0) === 1 ? t('events.gallery.itemCountOne', { count: a.item_count ?? 0 }) : t('events.gallery.itemCountOther', { count: a.item_count ?? 0 })}</div>
                </div>
              </div>`)}
          </div>`
          : html`<cadence-empty-state heading=${t('events.gallery.empty.heading')} message=${this.canEdit ? t('events.gallery.empty.edit') : t('events.gallery.empty.view')}></cadence-empty-state>`}
      </div>
      ${this.renderModal()}
    `;
  }

  private renderAlbumView() {
    const a = this.openAlbum!;
    return html`
      <div class="panel">
        <cadence-album-view
          .apiBase=${this.base}
          .albumId=${a.id}
          .canUpload=${this.canEdit}
          .canApprove=${this.canEdit}
          .canTag=${this.canEdit}
          .canSetCover=${this.canEdit}
          .canEditAlbum=${this.canEdit}
          .canDeleteAlbum=${this.canEdit}
          .showBack=${true}
          .backLabel=${t('events.gallery.allAlbums')}
          .tags=${this.allTags}
          @back=${() => this.closeAlbumView()}
          @edit-album=${() => this.openEdit(a)}
          @album-deleted=${() => this.closeAlbumView()}
          @tag-created=${(e: CustomEvent) => this.mergeTag(e.detail)}></cadence-album-view>
      </div>
      ${this.renderModal()}
    `;
  }

  private renderModal() {
    return html`
      <cadence-modal ?open=${this.modalOpen} heading=${this.editId ? t('events.gallery.editAlbum') : t('events.gallery.newAlbum')} @close=${() => (this.modalOpen = false)}>
        <div class="efield">
          <cadence-input label=${t('events.gallery.albumName')} .value=${this.form.name} @input=${(e: CustomEvent) => (this.form = { ...this.form, name: e.detail })}></cadence-input>
        </div>
        <div class="efield">
          <label>${t('events.gallery.descriptionOptional')}</label>
          <textarea .value=${this.form.description} @input=${(e: Event) => (this.form = { ...this.form, description: (e.target as HTMLTextAreaElement).value })}></textarea>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.modalOpen = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.save()}>${t('events.gallery.saveAlbum')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
